import React, { useState, useEffect, useRef } from 'react';

const CATEGORIES = [
  {
    label: 'AI & LLM',
    icon: '🤖',
    color: '#00B14F',
    techs: [
      { name: 'LangChain',    level: 95, badge: 'Expert' },
      { name: 'OpenAI GPT-4o',level: 92, badge: 'Expert' },
      { name: 'RAG Pipelines',level: 90, badge: 'Expert' },
      { name: 'LlamaIndex',   level: 85, badge: 'Advanced' },
      { name: 'Hugging Face', level: 78, badge: 'Advanced' },
      { name: 'Vector DBs',   level: 88, badge: 'Expert' },
    ],
  },
  {
    label: 'Cloud & DevOps',
    icon: '☁️',
    color: '#2563EB',
    techs: [
      { name: 'Azure',          level: 95, badge: 'Expert' },
      { name: 'Terraform / IaC',level: 88, badge: 'Expert' },
      { name: 'Docker / K8s',   level: 90, badge: 'Expert' },
      { name: 'GitHub Actions', level: 92, badge: 'Expert' },
      { name: 'Azure DevOps',   level: 88, badge: 'Expert' },
      { name: 'Azure AKS',      level: 85, badge: 'Advanced' },
    ],
  },
  {
    label: 'Backend',
    icon: '🔧',
    color: '#7C3AED',
    techs: [
      { name: '.NET 8',        level: 96, badge: 'Expert' },
      { name: 'Python / FastAPI',level: 88, badge: 'Expert' },
      { name: 'Node.js',       level: 82, badge: 'Advanced' },
      { name: 'PostgreSQL',    level: 90, badge: 'Expert' },
      { name: 'Redis',         level: 85, badge: 'Advanced' },
      { name: 'gRPC / SignalR',level: 80, badge: 'Advanced' },
    ],
  },
  {
    label: 'Frontend & Mobile',
    icon: '📱',
    color: '#D97706',
    techs: [
      { name: 'React / Next.js', level: 93, badge: 'Expert' },
      { name: 'Flutter',         level: 92, badge: 'Expert' },
      { name: 'TypeScript',      level: 90, badge: 'Expert' },
      { name: 'React Native',    level: 75, badge: 'Proficient' },
      { name: 'Tailwind CSS',    level: 88, badge: 'Expert' },
      { name: 'PWA',             level: 85, badge: 'Advanced' },
    ],
  },
];

const BADGE_COLOR = {
  Expert:    { bg: 'rgba(0,177,79,.12)',  color: '#00843D' },
  Advanced:  { bg: 'rgba(37,99,235,.1)',  color: '#1D4ED8' },
  Proficient:{ bg: 'rgba(124,58,237,.1)', color: '#6D28D9' },
};

function SkillBar({ name, level, badge, color, animate }) {
  return (
    <div className="skill-row">
      <div className="skill-info">
        <span className="skill-name">{name}</span>
        <span className="skill-badge" style={BADGE_COLOR[badge]}>{badge}</span>
      </div>
      <div className="skill-bar-track">
        <div
          className="skill-bar-fill"
          style={{
            width: animate ? `${level}%` : '0%',
            background: color,
            transition: animate ? 'width 1.2s cubic-bezier(.4,0,.2,1)' : 'none',
          }}
        />
        <span className="skill-pct">{level}%</span>
      </div>
    </div>
  );
}

export default function TechExpertise() {
  const [active, setActive] = useState(0);
  const [animate, setAnimate] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setAnimate(true); obs.unobserve(el); }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Re-animate on tab switch
  const switchTab = (i) => {
    setAnimate(false);
    setActive(i);
    setTimeout(() => setAnimate(true), 60);
  };

  const cat = CATEGORIES[active];

  return (
    <section className="expertise-sec" id="expertise" ref={ref} aria-label="Technology expertise">
      <div className="wrap">
        <div className="sec-hd rv">
          <div className="chip">💡 Deep Expertise</div>
          <h2>What We Actually Know</h2>
          <p>Not a Jack of all trades — genuine depth across the full modern stack.</p>
        </div>

        <div className="exp-layout">
          {/* Category tabs */}
          <div className="exp-cats rv">
            {CATEGORIES.map((c, i) => (
              <button
                key={c.label}
                className={`exp-cat ${active === i ? 'on' : ''}`}
                onClick={() => switchTab(i)}
                style={active === i ? { borderColor: c.color, background: `${c.color}12` } : {}}
              >
                <span className="exp-cat-icon">{c.icon}</span>
                <span className="exp-cat-label">{c.label}</span>
                {active === i && <span className="exp-cat-dot" style={{ background: c.color }} />}
              </button>
            ))}

            {/* Certifications */}
            <div className="exp-certs rv">
              <div className="exp-certs-title">🏆 Certifications</div>
              {[
                { icon: '☁️', name: 'Azure Solutions Architect' },
                { icon: '🤖', name: 'Azure AI Engineer' },
                { icon: '🔐', name: 'Azure Security Engineer' },
                { icon: '📊', name: 'Power BI Data Analyst' },
              ].map(cert => (
                <div key={cert.name} className="exp-cert">
                  <span>{cert.icon}</span>
                  <span>{cert.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Skills panel */}
          <div className="exp-skills rr">
            <div className="exp-skills-header" style={{ borderColor: `${cat.color}33`, background: `${cat.color}08` }}>
              <span className="exp-skills-icon">{cat.icon}</span>
              <span className="exp-skills-title" style={{ color: cat.color }}>{cat.label}</span>
              <span className="exp-skills-count">{cat.techs.length} technologies</span>
            </div>
            <div className="exp-skills-body">
              {cat.techs.map(t => (
                <SkillBar key={t.name} {...t} color={cat.color} animate={animate} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
